/**
 * Content loader.
 *
 * Loads module manifest and unit JSON over the network. Both are immutable
 * within a deploy (the build step content-hashes them via Vite), so we
 * happily cache them in memory for the session and lean on the HTTP cache
 * for cross-session.
 */

import type { ModuleCatalogEntry, ModuleManifest, Unit } from '../types';

const CONTENT_BASE = '/content';

const catalogCache = new Map<string, Promise<ModuleCatalogEntry[]>>();
const moduleCache = new Map<string, Promise<ModuleManifest>>();
const unitCache = new Map<string, Promise<Unit>>();

export function loadModules(): Promise<ModuleCatalogEntry[]> {
  let p = catalogCache.get('modules');
  if (!p) {
    p = fetch(`${CONTENT_BASE}/modules.json`).then(asJson<ModuleCatalogEntry[]>);
    catalogCache.set('modules', p);
  }
  return p;
}

export function loadModule(id: 'm1' | 'm2' | 'm3'): Promise<ModuleManifest> {
  let p = moduleCache.get(id);
  if (!p) {
    p = fetch(`${CONTENT_BASE}/${id}/module.json`).then(asJson<ModuleManifest>);
    moduleCache.set(id, p);
  }
  return p;
}

export function loadUnit(moduleId: 'm1' | 'm2' | 'm3', unitIndex: number): Promise<Unit> {
  const key = `${moduleId}-${pad2(unitIndex)}`;
  let p = unitCache.get(key);
  if (!p) {
    p = fetch(`${CONTENT_BASE}/${moduleId}/unit-${pad2(unitIndex)}.json`)
      .then(asJson<Unit>)
      .then(validateUnit);
    unitCache.set(key, p);
  }
  return p;
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    throw new Error(`Content fetch failed: ${res.status} ${res.url}`);
  }
  return (await res.json()) as T;
}

/**
 * Light runtime validation. Heavy schema checks run at build time
 * (`scripts/validate-content.ts`); this is a defence-in-depth check that
 * catches the cases where someone serves a manually-edited JSON file.
 */
function validateUnit(u: Unit): Unit {
  if (!Array.isArray(u.slides) || u.slides.length === 0) {
    throw new Error(`Unit ${u.id} must have at least one slide; got ${u.slides?.length}`);
  }
  for (let i = 0; i < u.slides.length; i++) {
    const s = u.slides[i]!;
    if (s.index !== i + 1) {
      throw new Error(`Unit ${u.id}: slide at position ${i} has index=${s.index}`);
    }
  }
  return u;
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}
